import { onTicketsConfirmed } from "@/lib/ticketsConfirmed";
import type { TicketReceipt } from "@shared/ticketReceipt";

/*
 * CHI-20 ticketing API surface (client side). The ONLY module that names
 * Dev2's procedures (TICKETING_PROCEDURES); a rename touches only this file.
 *
 * Contract v4 (/workspace/chi-lny/CHI-20-contract.md §3, Dev2 head d2ff1cf):
 *   events.ticketQuote({ slug })                  -> TicketQuote
 *   events.ticketCheckout({ eventSlug, attendees, promotionCode? }) -> { orderId, checkoutUrl, expiresAt, serverNow } | unavailable
 *   events.ticketPromotionQuote({ slug, code, quantity }) -> server-calculated promotion totals
 *   events.ticketCheckoutStatus({ orderId, sessionId }) -> { status, ticketCount }
 *   events.ticketOrderForRestore({ orderId })     -> { eventSlug, quantity, attendees, expiresAt, serverNow }
 *   events.releaseTicketCheckout({ orderId })     -> { released }
 *   events.ticketHoldStatus({ orderId })          -> { status, expiresAt, serverNow }   (buyer-only, no session)
 *   events.renewTicketHold({ orderId })            -> { status, expiresAt, serverNow, quantity }
 *   events.expireTicketHold({ orderId })            -> { status, expiresAt, serverNow, quantity }
 *   events.myTickets()                            -> { tickets: Ticket[], serverNow }
 *   events.myTicketsForEvent({ slug })            -> { tickets (webhook-issued active only), pendingOrder | null, hasBlockingTicket, serverNow }
 *   events.verifyCheckoutReturn({ orderId, sessionId }) -> { paid, paymentStatus, orderId, ticketsReady }  (CHI-26)
 * Quantity is attendees.length. The client NEVER sends a price.
 *
 * QR (contract open question 1, SETTLED by the CA): HMAC-derived tokens; active
 * tickets for their owner carry qrToken. The UI renders it only as a QR image and
 * never puts it in a URL, query string, route, log or storage.
 *
 * WIRING: the app uses createTrpcTicketingApi(trpcClient) (ticketingApiProvider.ts).
 * mockTicketingApi exists for tests only.
 */

export type Person = { name: string; email: string };
export type TicketAttendeeInput = Person;

/** v4 events.ticketQuote. */
export type TicketQuote = {
  unitPriceCents: number;
  fullPriceCents: number;
  currency: string;
  promoActive: boolean;
  promoEndsAt: string | null;
  seatsLeft: number | null;
  maxPerOrder: number | null;
  available: boolean;
  /** Optional server clock supplied by newer quote responses for expiry display. */
  serverNow?: string;
  nextPriceChangeAt?: string | null;
};

export type TicketPromotionQuote = {
  code: string;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  currency: string;
};

export type TicketCheckoutResult =
  | { status: "checkout"; orderId: number; checkoutUrl: string; expiresAt: string; serverNow: string }
  | { status: "unavailable"; reason: string };

export type TicketCheckoutStatus = {
  status: "pending" | "paid" | "expired" | "failed" | "refunded" | "canceled";
  ticketCount: number;
  expectedTicketCount?: number;
  needsReview?: boolean;
};

export type TicketOrderForRestore = {
  orderId?: number;
  status?: string;
  eventSlug: string;
  quantity: number;
  attendees: { name: string; email: string | null }[];
  expiresAt: string;
  serverNow: string;
  promotionCode?: string | null;
};

export type TicketHoldStatus = { status: string; expiresAt: string; serverNow: string; quantity?: number };

export type TicketStatus = "active" | "revoked" | "refunded" | "suspended" | "cancelled";

/** v4 Ticket. qrToken is present only on active tickets returned to their owner. */
export type Ticket = {
  ticketId: number;
  orderId: number;
  eventSlug: string;
  attendeeName: string;
  attendeeEmail: string | null;
  status: TicketStatus;
  eventStartsAt: string | null;
  isBuyerOwn: boolean;
  /** Held through a claim link; only the buyer can edit the guest. */
  claimedByViewer?: boolean;
  version?: number;
  checkedInAt?: string | null;
  qrToken?: string | null;
};
/** Back-compat alias. */
export type MyTicket = Ticket;

/** Stable, reversible raffle label derived from the server ticket ID (not a credential). */
export { formatTicketRaffleNumber } from "@shared/ticketDisplay";

/**
 * blockingEventSlugs (Dev2 0021): slugs where the viewer holds an ACTIVE or SUSPENDED ticket.
 * Drives Buy Tickets on list surfaces only; tickets[] stays active-only. Optional: older servers omit it.
 */
export type MyTicketsResult = { tickets: Ticket[]; blockingEventSlugs?: string[]; receipts?: TicketReceipt[]; serverNow: string };
export type PendingOrder = { orderId: number; status: string; quantity?: number; expiresAt?: string };
/**
 * hasBlockingTicket (Dev2, CA-approved, SM 1:47 PM CT): true when the viewer has an ACTIVE or
 * SUSPENDED ticket for the event; drives Buy Tickets visibility only (tickets[] stays active-only).
 * SEAM: optional until Dev2's 0007+ lands on chi-int-20; see useMyTicketsForEvent for the fallback.
 */
export type MyTicketsForEvent = { tickets: Ticket[]; pendingOrder: PendingOrder | null; hasBlockingTicket?: boolean; serverNow?: string; /** CompleteTicketPurchase (temporary): Dev2, optional, missing = false. */ viewerTicketing?: { completePurchaseEligible?: boolean } };

/** CHI-26 events.verifyCheckoutReturn: Stripe session read server-side; never issues tickets. */
export type CheckoutReturn = { paid: boolean; paymentStatus: "paid" | "unpaid" | "no_payment_required"; orderId: number; ticketsReady: boolean };

export type BuyerVerificationStart =
  | { status: "sent"; mode: "sign_in" | "create_account" }
  | { status: "rate_limited"; minutes: number }
  | { status: "error" };

export type BuyerVerificationResult =
  | { status: "verified"; mode: "sign_in" | "create_account"; accountName?: string | null }
  | { status: "totp_required"; continuation: unknown }
  | { status: "wrong" | "expired" | "error" }
  | { status: "rate_limited"; minutes: number };

export type TicketingApi = {
  ticketQuote(input: { slug: string }): Promise<TicketQuote>;
  ticketPromotionQuote(input: { slug: string; code: string; quantity: number }): Promise<TicketPromotionQuote>;
  ticketCheckout(input: { eventSlug: string; attendees: Person[]; promotionCode?: string }): Promise<TicketCheckoutResult>;
  ticketCheckoutStatus(input: { orderId: number; sessionId: string }): Promise<TicketCheckoutStatus>;
  ticketOrderForRestore(input: { orderId: number }): Promise<TicketOrderForRestore | null>;
  releaseTicketCheckout(input: { orderId: number }): Promise<{ released: number }>;
  ticketHoldStatus(input: { orderId: number }): Promise<TicketHoldStatus>;
  renewTicketHold(input: { orderId: number }): Promise<TicketHoldStatus>;
  expireTicketHold(input: { orderId: number }): Promise<TicketHoldStatus>;
  myTickets(): Promise<MyTicketsResult>;
  myTicketsForEvent(input: { slug: string }): Promise<MyTicketsForEvent>;
  /** CHI-26. NOT_FOUND on wrong buyer / session mismatch. */
  verifyCheckoutReturn(input: { orderId: number; sessionId: string }): Promise<CheckoutReturn>;
};

export const TICKETING_PROCEDURES = {
  quote: "ticketQuote",
  promotionQuote: "ticketPromotionQuote",
  checkout: "ticketCheckout",
  status: "ticketCheckoutStatus",
  restore: "ticketOrderForRestore",
  release: "releaseTicketCheckout",
  hold: "ticketHoldStatus",
  renewHold: "renewTicketHold",
  expireHold: "expireTicketHold",
  mine: "myTickets",
  forEvent: "myTicketsForEvent",
  verifyReturn: "verifyCheckoutReturn",
} as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createTrpcTicketingApi(client: any): TicketingApi {
  const ev = () => client.events;
  const P = TICKETING_PROCEDURES;
  return {
    ticketQuote: input => ev()[P.quote].query(input),
    ticketPromotionQuote: input => ev()[P.promotionQuote].mutate(input),
    ticketCheckout: input => ev()[P.checkout].mutate(input),
    ticketCheckoutStatus: input => ev()[P.status].query(input),
    ticketOrderForRestore: input => ev()[P.restore].query(input),
    releaseTicketCheckout: input => ev()[P.release].mutate(input),
    ticketHoldStatus: input => ev()[P.hold].query(input),
    renewTicketHold: input => ev()[P.renewHold].mutate(input),
    expireTicketHold: input => ev()[P.expireHold].mutate(input),
    myTickets: () => ev()[P.mine].query(),
    myTicketsForEvent: input => ev()[P.forEvent].query(input),
    verifyCheckoutReturn: input => ev()[P.verifyReturn].query(input),
  };
}

/* ── Config ─────────────────────────────────────────────────────────────── */

/** D3 (John): max 10; the server's maxPerOrder wins when present. */
export const MAX_TICKETS_PER_ORDER = 10;
/** D7 (John): an email that already has an account is signed in by the same code. */
export const OTP_SIGNS_IN_EXISTING_ACCOUNT = true;
/** CHI-28 support address (John: info@thechialliance.com). Mirrors server/_core/env.ts SUPPORT_EMAIL. */
export const DEFAULT_SUPPORT_EMAIL = "info@thechialliance.com";
/** Vite env VITE_SUPPORT_EMAIL; blank or unset falls back to the default. */
export function resolveSupportEmail(env: Readonly<Record<string, unknown>> = {}): string {
  const raw = env.VITE_SUPPORT_EMAIL;
  const v = typeof raw === "string" ? raw.trim() : "";
  return v || DEFAULT_SUPPORT_EMAIL;
}
/** Typed string | null so a caller can still pass null to hide the sentence. */
export const SUPPORT_EMAIL: string | null = resolveSupportEmail(import.meta.env);
export const OTP_RESEND_SECONDS = 30;

/** Shared Stripe success path (contract v4): /account/events?order=<id>&session_id={CHECKOUT_SESSION_ID}. */
export const TICKETS_SUCCESS_PATH = "/account/events";
export const TICKETS_SESSION_PARAM = "session_id";
export const TICKETS_ORDER_PARAM = "order";
export const TICKETS_CANCELLED_PARAM = "checkout";
export const TICKETS_CANCELLED_VALUE = "cancelled";
const TICKET_JOIN_INTEREST_PREFIX = "chi_ticket_join_interest_v1:";

/** Session-only opt-in marker: event slug in the key, boolean value only. */
export function ticketJoinInterestStorageKey(eventSlug: string) {
  return `${TICKET_JOIN_INTEREST_PREFIX}${encodeURIComponent(eventSlug)}`;
}

export function readTicketJoinInterest(eventSlug: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(ticketJoinInterestStorageKey(eventSlug)) === "1";
  } catch {
    return false;
  }
}

export function writeTicketJoinInterest(eventSlug: string, wantsToJoin: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const key = ticketJoinInterestStorageKey(eventSlug);
    if (wantsToJoin) window.sessionStorage.setItem(key, "1");
    else window.sessionStorage.removeItem(key);
  } catch {
    // Consent persistence is only a same-tab convenience; checkout itself remains available.
  }
}

export function clearTicketJoinInterests(): void {
  if (typeof window === "undefined") return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(TICKET_JOIN_INTEREST_PREFIX)) keys.push(key);
    }
    keys.forEach(key => window.sessionStorage.removeItem(key));
  } catch {
    // A storage failure must never hold up a purchaser's email change.
  }
}

/** CHI-27: poll every 3s for up to 60s (configurable). CHI-28: slow recheck every 15s after. */
export const FINALIZING_POLL_INTERVAL_MS = 3_000;
export const FINALIZING_WINDOW_MS = 60_000;
export const FINALIZING_BACKGROUND_INTERVAL_MS = 15_000;

/* ── Mock ───────────────────────────────────────────────────────────────── */

const PROMO_END = "2027-01-01T06:00:00Z"; // 2027-01-01 00:00 CT

export const mockTicketingApi: TicketingApi = {
  async ticketQuote() {
    const promoActive = Date.now() < Date.parse(PROMO_END);
    return { unitPriceCents: promoActive ? 7900 : 9900, fullPriceCents: 9900, currency: "usd", promoActive, promoEndsAt: PROMO_END, seatsLeft: null, maxPerOrder: MAX_TICKETS_PER_ORDER, available: true };
  },
  async ticketPromotionQuote() {
    return Promise.reject(new Error("Promotion codes are unavailable in the development ticketing API."));
  },
  async ticketCheckout(input) {
    const now = new Date();
    return { status: "checkout", orderId: 1, checkoutUrl: `https://checkout.stripe.test/mock?event=${encodeURIComponent(input.eventSlug)}`, expiresAt: new Date(now.getTime() + 30 * 60_000).toISOString(), serverNow: now.toISOString() };
  },
  async ticketCheckoutStatus() {
    return { status: "pending", ticketCount: 0 };
  },
  async ticketOrderForRestore() {
    return null;
  },
  async releaseTicketCheckout() {
    return { released: 0 };
  },
  async ticketHoldStatus() {
    const now = new Date();
    return { status: "pending", expiresAt: new Date(now.getTime() + 8 * 60_000).toISOString(), serverNow: now.toISOString(), quantity: 1 };
  },
  async renewTicketHold() {
    const now = new Date();
    return { status: "pending", expiresAt: new Date(now.getTime() + 8 * 60_000).toISOString(), serverNow: now.toISOString(), quantity: 1 };
  },
  async expireTicketHold() {
    const now = new Date().toISOString();
    return { status: "expired", expiresAt: now, serverNow: now, quantity: 1 };
  },
  async myTickets() {
    return { tickets: [], blockingEventSlugs: [], serverNow: new Date().toISOString() };
  },
  async myTicketsForEvent() {
    return { tickets: [], pendingOrder: null, hasBlockingTicket: false, serverNow: new Date().toISOString() };
  },
  async verifyCheckoutReturn(input) {
    return { paid: false, paymentStatus: "unpaid", orderId: input.orderId, ticketsReady: false };
  },
};

/**
 * CHI-20 (Dev2 0033/0034): the ONE place ticketCheckout errors are classified. Matched on the
 * MESSAGE PREFIX, never on `cause` (tRPC does not ship cause to the browser). When the error also
 * carries a tRPC code it must agree; when it carries none, the prefix alone decides.
 */
export type CheckoutErrorKind = "checkout_disabled" | "rate_limited" | "pending_order_exists" | "buyer_already_ticketed" | "attendee_already_registered" | "attendee_already_ticketed" | "attendee_ambiguous_email" | "complete_purchase_ineligible" | "event_ended" | "buyer_email_locked" | "other";
const CHECKOUT_ERROR_RULES: ReadonlyArray<{ kind: Exclude<CheckoutErrorKind, "other">; code: string | null; prefix: string }> = [
  { kind: "checkout_disabled", code: "PRECONDITION_FAILED", prefix: "tickets_checkout_disabled" },
  { kind: "rate_limited", code: "TOO_MANY_REQUESTS", prefix: "tickets_checkout_rate_limited" },
  { kind: "pending_order_exists", code: "CONFLICT", prefix: "tickets_pending_order_exists" },
  // CompleteTicketPurchase codes (SM contract answers, 2026-10-31). Prefix first; a present tRPC code must agree.
  { kind: "buyer_already_ticketed", code: "CONFLICT", prefix: "tickets_buyer_already_ticketed" },
  { kind: "attendee_already_registered", code: "CONFLICT", prefix: "tickets_attendee_already_registered" },
  // Fallback only: the server now sends tickets_attendee_already_registered for guests who match a member.
  { kind: "attendee_already_ticketed", code: "CONFLICT", prefix: "tickets_attendee_already_ticketed" },
  // Fallback only: Dev2 is likely retiring tickets_attendee_ambiguous_email.
  { kind: "attendee_ambiguous_email", code: "BAD_REQUEST", prefix: "tickets_attendee_ambiguous_email" },
  { kind: "complete_purchase_ineligible", code: "FORBIDDEN", prefix: "tickets_complete_purchase_ineligible" },
  { kind: "event_ended", code: "BAD_REQUEST", prefix: "tickets_event_ended" },
  { kind: "buyer_email_locked", code: "BAD_REQUEST", prefix: "tickets_buyer_email_locked" },
];
export const CHECKOUT_DISABLED_MESSAGE = "Ticket sales aren't open yet. Please check back soon.";
export const CHECKOUT_RATE_LIMITED_MESSAGE = "Too many attempts. Please try again shortly.";
export const ATTENDEE_AMBIGUOUS_EMAIL_MESSAGE_BASE = "We couldn't match one of these emails to a single account. Please use a different email";

/** Known, safe promo refusals from checkout; arbitrary server/Stripe prose stays hidden. */
export function promotionCheckoutErrorMessage(error: unknown): string | null {
  const e = error as { message?: unknown; data?: { code?: unknown } | null } | null | undefined;
  if (e?.data?.code !== "BAD_REQUEST" || typeof e.message !== "string") return null;
  if (/^This promo code is invalid\b/i.test(e.message)) {
    return "That promotion code can't be used for this order. Check the code or remove it to continue.";
  }
  if (/^Enter this first-purchase code on Stripe['’]s secure payment page\b/i.test(e.message)) {
    return "This first-purchase code must be entered on Stripe's secure payment page. Remove it here, then continue.";
  }
  return null;
}

/**
 * Which attendee row(s) a tickets_attendee_* refusal refers to. Contract (SM): data.attendees =
 * [{ index, name }] for already_registered (no email) and [{ index, name, email }] for
 * already_ticketed. Read defensively, in order: data.attendees[] / data.attendee / cause.attendees[],
 * then a short message tail after the prefix. Label = name, else "Attendee N" from index + 1.
 * NEVER an email (QA1): an email field is ignored, and string items / tail parts that look like an
 * email are dropped. Anything unusable -> [] (generic sentence).
 */
export function refusedAttendees(error: unknown, prefix = "tickets_attendee_already_ticketed"): string[] {
  const e = error as { message?: unknown; data?: Record<string, unknown> | null; cause?: Record<string, unknown> | null } | null | undefined;
  const pick = (v: unknown) => (typeof v === "string" && v.trim() && v.trim().length <= 120 && !v.includes("@") ? v.trim() : null);
  const label = (a: unknown): string | null => {
    if (typeof a === "string") return pick(a);
    if (!a || typeof a !== "object") return null;
    const o = a as { name?: unknown; index?: unknown };
    const name = pick(o.name);
    if (name) return name;
    return typeof o.index === "number" && Number.isInteger(o.index) && o.index >= 0 ? `Attendee ${o.index + 1}` : null;
  };
  for (const src of [e?.data, e?.cause]) {
    if (!src || typeof src !== "object") continue;
    const list = Array.isArray(src.attendees) ? src.attendees : src.attendee ? [src.attendee] : [];
    const names = list.map(label).filter((x): x is string => Boolean(x));
    if (names.length) return Array.from(new Set(names));
  }
  if (typeof e?.message === "string" && e.message.startsWith(prefix)) {
    const tail = e.message.slice(prefix.length).replace(/^:?\s*/, "").trim();
    // Only short identifiers, never a sentence of server prose.
    if (tail && !/[.!?]$/.test(tail) && !/\s(already|has|have|is|are)\s/i.test(` ${tail} `)) {
      const parts = tail.split(/\s*[,;]\s*/).map(pick).filter((x): x is string => Boolean(x));
      if (parts.length) return Array.from(new Set(parts));
    }
  }
  return [];
}
/** Back-compat name (0001 tests/callers). */
export const alreadyTicketedAttendees = (error: unknown) => refusedAttendees(error, "tickets_attendee_already_ticketed");


/** John's exact sentence (SM). Always present verbatim; the row name(s) lead when known. */
export const ATTENDEE_ALREADY_REGISTERED_MESSAGE = "This person is already registered. They need to log in to complete their own ticket purchase.";
/**
 * tickets_attendee_already_registered (CONFLICT; whole cart refused), and the already_ticketed
 * fallback (CP-UI-12: identical copy). Names only (name, else "Attendee N"), never emails, then
 * John's sentence verbatim:
 *   "Guest One: This person is ..."  /  "Guest One, Guest Two: This person is ..."  /  the sentence alone.
 */
export function attendeeAlreadyRegisteredMessage(error: unknown, prefix = "tickets_attendee_already_registered"): string {
  const who = refusedAttendees(error, prefix);
  return who.length === 0 ? ATTENDEE_ALREADY_REGISTERED_MESSAGE : `${who.join(", ")}: ${ATTENDEE_ALREADY_REGISTERED_MESSAGE}`;
}

/** tickets_buyer_already_ticketed (CONFLICT): the buyer already holds tickets; the cart links to View My Ticket(s). */
export const BUYER_ALREADY_TICKETED_MESSAGE = "You already have tickets for this event. Use View My Ticket(s) to see them.";


/** Plain sentence for tickets_attendee_ambiguous_email. */
export function attendeeAmbiguousEmailMessage(): string {
  return SUPPORT_EMAIL ? `${ATTENDEE_AMBIGUOUS_EMAIL_MESSAGE_BASE} or contact ${SUPPORT_EMAIL}.` : `${ATTENDEE_AMBIGUOUS_EMAIL_MESSAGE_BASE} or contact support.`;
}

export const COMPLETE_PURCHASE_INELIGIBLE_MESSAGE = "This account can't complete a ticket purchase for this event.";
export const EVENT_ENDED_MESSAGE = "This event has ended.";
export const BUYER_EMAIL_LOCKED_MESSAGE = "Your own attendee row has to use your account email.";

/**
 * One plain sentence for the cart-level refusals (whole cart refused, form kept). Null for every
 * other kind, which keeps its existing handling (disabled / rate limited / pending order = inline 409).
 */
export function attendeeCheckoutErrorMessage(error: unknown): string | null {
  switch (classifyCheckoutError(error)) {
    case "attendee_already_registered": return attendeeAlreadyRegisteredMessage(error);
    // CP-UI-12: fallback, rendered with the same copy as already_registered.
    case "attendee_already_ticketed": return attendeeAlreadyRegisteredMessage(error, "tickets_attendee_already_ticketed");
    case "buyer_already_ticketed": return BUYER_ALREADY_TICKETED_MESSAGE;
    case "attendee_ambiguous_email": return attendeeAmbiguousEmailMessage();
    case "complete_purchase_ineligible": return COMPLETE_PURCHASE_INELIGIBLE_MESSAGE;
    case "event_ended": return EVENT_ENDED_MESSAGE;
    case "buyer_email_locked": return BUYER_EMAIL_LOCKED_MESSAGE;
    default: return null;
  }
}

export function classifyCheckoutError(error: unknown): CheckoutErrorKind {
  const e = error as { message?: unknown; data?: { code?: unknown } | null } | null | undefined;
  const message = typeof e?.message === "string" ? e.message : "";
  const code = typeof e?.data?.code === "string" ? e.data.code : null;
  for (const rule of CHECKOUT_ERROR_RULES) {
    if (message.startsWith(rule.prefix) && (code === null || rule.code === null || code === rule.code)) return rule.kind;
  }
  return "other";
}

let current: TicketingApi | null = null;
let realFactory: (() => TicketingApi) | null = null;
/** Called once by ticketingApiProvider with the app's tRPC vanilla client. */
export function registerRealTicketingApi(factory: () => TicketingApi) {
  realFactory = factory;
}
export function getTicketingApi(): TicketingApi {
  if (current) return current;
  if (realFactory) return (current = realFactory());
  return unwiredTicketingApi;
}

/** Keep open event/purchase pages aligned with the server's scheduled price. */
export function watchTicketQuote(slug: string, onQuote: (quote: TicketQuote) => void, onError: () => void) {
  let active = true;
  let request = 0;
  let boundary: ReturnType<typeof setTimeout> | undefined;
  async function refresh() {
    const currentRequest = ++request;
    try {
      const quote = await getTicketingApi().ticketQuote({ slug });
      if (!active || currentRequest !== request) return;
      clearTimeout(boundary);
      onQuote(quote);
      const next = quote.nextPriceChangeAt ?? (quote.promoActive ? quote.promoEndsAt : null);
      const delay = next ? Date.parse(next) - (quote.serverNow ? Date.parse(quote.serverNow) : Date.now()) : NaN;
      if (Number.isFinite(delay) && delay > 0 && delay < 2_147_000_000) {
        boundary = setTimeout(refresh, delay + 25);
      }
    } catch {
      if (active && currentRequest === request) {
        clearTimeout(boundary);
        onError();
      }
    }
  }
  const visibleRefresh = () => { if (document.visibilityState !== "hidden") void refresh(); };
  const stopConfirmedListener = onTicketsConfirmed(slugs => {
    if (slugs.includes(slug)) visibleRefresh();
  });
  void refresh();
  const interval = setInterval(visibleRefresh, 30_000);
  window.addEventListener("focus", visibleRefresh);
  document.addEventListener("visibilitychange", visibleRefresh);
  return () => {
    active = false;
    request++;
    stopConfirmedListener();
    clearTimeout(boundary);
    clearInterval(interval);
    window.removeEventListener("focus", visibleRefresh);
    document.removeEventListener("visibilitychange", visibleRefresh);
  };
}

/** No tRPC provider (e.g. an isolated test): every call rejects, so UIs stay in their empty states. Never mock data. */
const unwired = () => Promise.reject(new Error("ticketing API not wired"));
const unwiredTicketingApi: TicketingApi = {
  ticketQuote: unwired, ticketPromotionQuote: unwired, ticketCheckout: unwired, ticketCheckoutStatus: unwired, ticketOrderForRestore: unwired,
  releaseTicketCheckout: unwired, ticketHoldStatus: unwired, renewTicketHold: unwired, expireTicketHold: unwired, myTickets: unwired, myTicketsForEvent: unwired, verifyCheckoutReturn: unwired,
};
export function setTicketingApi(api: TicketingApi) {
  current = api;
}
